import React from "react";
import { Col, Row } from "reactstrap";
import * as Yup from "yup";
import * as moment from "moment";

import {
  addContractorRecord,
  addContractorRegistration,
  ContractorRecordKind,
  editContractorRecord,
  editContractorRegistration,
  removeContractorRecord,
  removeContractorRegistration,
} from "common/services/contractors.service";
import { getPerformanceRatingTypes } from "common/services/performance-rating.service";
import { normalize } from "common/lib/normalize";
import { tidyCountry } from "Components/Common/CountrySelect";
import {
  RatingOptionLabel,
  ratingTone,
  ratingTypeOption,
} from "Components/Common/PerformanceRatingTone";
import {
  AsyncSelectField,
  CountryField,
  DateField,
  Option,
  OptionsSelectField,
  PeriodField,
  SwitchField,
  TextField,
} from "Components/Common/RecordManager/fields";
import {
  agencyCache,
  endAfterStart,
  initials,
  joinMeta,
  loadAgencies,
  loadContractors,
  loadPersons,
  option,
  period,
  toApiDate,
  toDate,
  useReferenceOptions,
} from "Components/Common/RecordManager/helpers";
import {
  FieldsProps,
  RecordKindConfig,
  RecordStatus,
  RecordStore,
} from "Components/Common/RecordManager/types";

/** The contractor profile's record kinds for the shared record manager. */

/** Saves through the per-record endpoints under the contractor. */
const relatedRecordStore = (kind: ContractorRecordKind): RecordStore => ({
  add: async (contractor, payload) => {
    const response: any = await addContractorRecord(contractor.id, kind, payload);
    return response?.data?.id ?? null;
  },
  edit: async (contractor, record, payload) => {
    await editContractorRecord(contractor.id, kind, record.id, payload);
  },
  remove: async (contractor, record) => {
    await removeContractorRecord(contractor.id, kind, record.id);
  },
});

/** Permission actions on the contractor API's related-record controller. */
const relatedRecordPermissions = (add: string, edit: string, remove: string) => ({
  resource: "contractor",
  controller: "related-record",
  add,
  edit,
  remove,
});

const ratingTypeCache: { current: Option[] | null } = { current: null };
const loadRatingTypes = async () => {
  const response: any = await getPerformanceRatingTypes({});
  return normalize(response.data).map(ratingTypeOption);
};


// ── board members ────────────────────────────────────────────────────────────

const BoardMemberFields = ({ form, readOnly }: FieldsProps) => (
  <Row>
    <Col md={12}>
      <AsyncSelectField
        form={form}
        name="person"
        label="Person"
        required
        readOnly={readOnly}
        placeholder="Search people by name"
        loadOptions={loadPersons()}
        hint="People are kept in the register. Add them under Persons first if they are not listed."
      />
    </Col>
    <Col md={12}>
      <TextField
        form={form}
        name="role"
        label="Role"
        readOnly={readOnly}
        placeholder="For example Chairperson, Director, Finance officer"
      />
    </Col>
    <Col md={6}>
      <DateField form={form} name="fromDate" label="From" readOnly={readOnly} />
    </Col>
    <Col md={6}>
      <DateField
        form={form}
        name="toDate"
        label="To"
        readOnly={readOnly}
        hint="Leave empty while they are still in the role."
      />
    </Col>
  </Row>
);

export const BOARD_MEMBERS: RecordKindConfig = {
  kind: "board-members",
  title: "Staff and board of directors",
  noun: "member",
  permissions: relatedRecordPermissions("addBoardMember", "editBoardMember", "removeBoardMember"),
  store: relatedRecordStore("board-members"),
  records: (contractor) =>
    [...(contractor?.boardOfDirectors || [])].sort((a, b) =>
      (a?.person?.fullName || "").localeCompare(b?.person?.fullName || "")
    ),
  summary: (record) => ({
    title: record?.person?.fullName || "Unnamed person",
    meta: joinMeta(record?.role, period(record?.fromDate, record?.toDate)),
    avatar: { initials: initials(record?.person?.fullName), tone: "primary" },
  }),
  profileLink: (record) =>
    record?.person?.id
      ? { to: `/persons/${record.person.id}`, label: "Open person" }
      : null,
  emptyValues: { person: null, role: "", fromDate: null, toDate: null },
  toValues: (record) => ({
    person: option(record?.person?.id, record?.person?.fullName),
    role: record?.role || "",
    fromDate: toDate(record?.fromDate),
    toDate: toDate(record?.toDate),
  }),
  toPayload: (values) => ({
    person: values.person?.value ?? null,
    role: values.role,
    fromDate: toApiDate(values.fromDate),
    toDate: toApiDate(values.toDate),
  }),
  schema: Yup.object({
    person: Yup.object().nullable().required("Choose a person"),
    role: Yup.string().nullable(),
    fromDate: Yup.date().nullable(),
    toDate: endAfterStart("fromDate"),
  }),
  Fields: BoardMemberFields,
  removeNote: (record) =>
    `This deletes the entry from the contractor. ${
      record?.person?.fullName || "The person"
    }'s own record stays in the register.`,
};

// ── affiliations to contractors ──────────────────────────────────────────────

const ContractorAffiliationFields = ({ form, readOnly, ownerId }: FieldsProps) => (
  <Row>
    <Col md={12}>
      <AsyncSelectField
        form={form}
        name="affiliatedTo"
        label="Affiliated contractor"
        required
        readOnly={readOnly}
        placeholder="Search contractors by name"
        loadOptions={loadContractors(ownerId)}
      />
    </Col>
    <Col md={6}>
      <TextField
        form={form}
        name="natureOfRelationship"
        label="Nature of relationship"
        readOnly={readOnly}
        placeholder="For example Parent company, Joint venture"
      />
    </Col>
    <Col md={6}>
      <TextField
        form={form}
        name="informationSource"
        label="Information source"
        readOnly={readOnly}
        placeholder="Where this was learned"
      />
    </Col>
    <Col md={12}>
      <SwitchField
        form={form}
        name="relationshipConfirmed"
        label="Relationship confirmed"
        readOnly={readOnly}
      />
    </Col>
  </Row>
);

const relationshipMeta = (record: any) =>
  joinMeta(
    record?.natureOfRelationship,
    record?.relationshipConfirmed ? "Confirmed" : "Not confirmed"
  );

export const CONTRACTOR_AFFILIATIONS: RecordKindConfig = {
  kind: "contractor-affiliations",
  title: "Affiliations to contractors",
  noun: "affiliation",
  permissions: relatedRecordPermissions("addContractorAffiliation", "editContractorAffiliation", "removeContractorAffiliation"),
  store: relatedRecordStore("contractor-affiliations"),
  records: (contractor) =>
    [...(contractor?.contractorAffiliations || [])].sort((a, b) =>
      (a?.affiliatedTo?.contractorName || "").localeCompare(
        b?.affiliatedTo?.contractorName || ""
      )
    ),
  summary: (record) => ({
    title: record?.affiliatedTo?.contractorName || "Unknown contractor",
    meta: relationshipMeta(record),
    avatar: { icon: "ri-building-line", tone: "primary" },
  }),
  profileLink: (record) =>
    record?.affiliatedTo?.id
      ? { to: `/contractors/${record.affiliatedTo.id}`, label: "Open contractor" }
      : null,
  emptyValues: {
    affiliatedTo: null,
    natureOfRelationship: "",
    informationSource: "",
    relationshipConfirmed: false,
  },
  toValues: (record) => ({
    affiliatedTo: option(record?.affiliatedTo?.id, record?.affiliatedTo?.contractorName),
    natureOfRelationship: record?.natureOfRelationship || "",
    informationSource: record?.informationSource || "",
    relationshipConfirmed: !!record?.relationshipConfirmed,
  }),
  toPayload: (values) => ({
    affiliatedTo: values.affiliatedTo?.value ?? null,
    natureOfRelationship: values.natureOfRelationship,
    informationSource: values.informationSource,
    relationshipConfirmed: !!values.relationshipConfirmed,
  }),
  schema: Yup.object({
    affiliatedTo: Yup.object().nullable().required("Choose a contractor"),
  }),
  Fields: ContractorAffiliationFields,
  removeNote: (record) =>
    `This deletes the affiliation only. ${
      record?.affiliatedTo?.contractorName || "The other contractor"
    } stays in the register.`,
  footnote: (contractor) => {
    const count = contractor?.contractorAffiliatedTo?.length || 0;
    if (!count) return null;
    return count === 1
      ? "1 more affiliation was recorded on the other contractor's profile and is edited there."
      : `${count} more affiliations were recorded on the other contractors' profiles and are edited there.`;
  },
};

// ── affiliations to persons ──────────────────────────────────────────────────

const PersonAffiliationFields = ({ form, readOnly }: FieldsProps) => (
  <Row>
    <Col md={12}>
      <AsyncSelectField
        form={form}
        name="person"
        label="Person"
        required
        readOnly={readOnly}
        placeholder="Search people by name"
        loadOptions={loadPersons()}
      />
    </Col>
    <Col md={6}>
      <TextField
        form={form}
        name="natureOfRelationship"
        label="Nature of relationship"
        readOnly={readOnly}
        placeholder="For example Owner, Shareholder, Relative"
      />
    </Col>
    <Col md={6}>
      <TextField
        form={form}
        name="informationSource"
        label="Information source"
        readOnly={readOnly}
        placeholder="Where this was learned"
      />
    </Col>
    <Col md={12}>
      <SwitchField
        form={form}
        name="relationshipConfirmed"
        label="Relationship confirmed"
        readOnly={readOnly}
      />
    </Col>
  </Row>
);

export const PERSON_AFFILIATIONS: RecordKindConfig = {
  kind: "person-affiliations",
  title: "Affiliations to persons",
  noun: "affiliation",
  permissions: relatedRecordPermissions("addPersonAffiliation", "editPersonAffiliation", "removePersonAffiliation"),
  store: relatedRecordStore("person-affiliations"),
  records: (contractor) =>
    [...(contractor?.personAffiliations || [])].sort((a, b) =>
      (a?.person?.fullName || "").localeCompare(b?.person?.fullName || "")
    ),
  summary: (record) => ({
    title: record?.person?.fullName || "Unnamed person",
    meta: relationshipMeta(record),
    avatar: { initials: initials(record?.person?.fullName), tone: "primary" },
  }),
  profileLink: (record) =>
    record?.person?.id
      ? { to: `/persons/${record.person.id}`, label: "Open person" }
      : null,
  emptyValues: {
    person: null,
    natureOfRelationship: "",
    informationSource: "",
    relationshipConfirmed: false,
  },
  toValues: (record) => ({
    person: option(record?.person?.id, record?.person?.fullName),
    natureOfRelationship: record?.natureOfRelationship || "",
    informationSource: record?.informationSource || "",
    relationshipConfirmed: !!record?.relationshipConfirmed,
  }),
  toPayload: (values) => ({
    person: values.person?.value ?? null,
    natureOfRelationship: values.natureOfRelationship,
    informationSource: values.informationSource,
    relationshipConfirmed: !!values.relationshipConfirmed,
  }),
  schema: Yup.object({
    person: Yup.object().nullable().required("Choose a person"),
  }),
  Fields: PersonAffiliationFields,
  removeNote: (record) =>
    `This deletes the affiliation only. ${
      record?.person?.fullName || "The person"
    }'s own record stays in the register.`,
};

// ── performance ratings ──────────────────────────────────────────────────────

const PerformanceRatingFields = ({ form, readOnly }: FieldsProps) => {
  const agencies = useReferenceOptions(loadAgencies, agencyCache);
  const ratingTypes = useReferenceOptions(loadRatingTypes, ratingTypeCache);
  return (
    <Row>
      <Col md={6}>
        <OptionsSelectField
          form={form}
          name="performanceRatingType"
          label="Rating"
          required
          readOnly={readOnly}
          placeholder="Select a rating"
          options={ratingTypes.options}
          isLoading={ratingTypes.loading}
          formatOptionLabel={RatingOptionLabel}
        />
      </Col>
      <Col md={6}>
        <OptionsSelectField
          form={form}
          name="ratedBy"
          label="Rated by"
          required
          readOnly={readOnly}
          placeholder="Select an agency"
          options={agencies.options}
          isLoading={agencies.loading}
        />
      </Col>
      <Col md={6}>
        <DateField form={form} name="from" label="From" required readOnly={readOnly} />
      </Col>
      <Col md={6}>
        <DateField
          form={form}
          name="to"
          label="To"
          readOnly={readOnly}
          hint="Leave empty if the rating has no end date."
        />
      </Col>
      <Col md={12}>
        <TextField
          form={form}
          name="description"
          label="Reason"
          multiline
          readOnly={readOnly}
          placeholder="What the rating is based on"
        />
      </Col>
    </Row>
  );
};

export const PERFORMANCE_RATINGS: RecordKindConfig = {
  kind: "performance-ratings",
  title: "Performance ratings",
  noun: "rating",
  permissions: relatedRecordPermissions("addPerformanceRating", "editPerformanceRating", "removePerformanceRating"),
  store: relatedRecordStore("performance-ratings"),
  records: (contractor) =>
    [...(contractor?.performanceRatings || [])].sort(
      (a, b) =>
        new Date(b?.from || b?.createdAt || 0).getTime() -
        new Date(a?.from || a?.createdAt || 0).getTime()
    ),
  summary: (record) => ({
    title: record?.performanceRatingType?.name || "Unrated",
    meta: joinMeta(record?.ratedBy?.name, period(record?.from, record?.to)),
    avatar: {
      icon: "ri-star-line",
      tone: ratingTone(record?.performanceRatingType),
    },
  }),
  emptyValues: {
    performanceRatingType: null,
    ratedBy: null,
    from: null,
    to: null,
    description: "",
  },
  toValues: (record) => ({
    performanceRatingType: record?.performanceRatingType?.id
      ? ratingTypeOption(record.performanceRatingType)
      : null,
    ratedBy: option(record?.ratedBy?.id, record?.ratedBy?.name),
    from: toDate(record?.from),
    to: toDate(record?.to),
    description: record?.description || "",
  }),
  toPayload: (values) => ({
    performanceRatingType: values.performanceRatingType?.value ?? null,
    ratedBy: values.ratedBy?.value ?? null,
    from: toApiDate(values.from),
    to: toApiDate(values.to),
    description: values.description,
  }),
  schema: Yup.object({
    performanceRatingType: Yup.object().nullable().required("Choose a rating"),
    ratedBy: Yup.object().nullable().required("Choose the rating agency"),
    from: Yup.date().nullable().required("Choose the start date"),
    to: endAfterStart("from"),
  }),
  Fields: PerformanceRatingFields,
  removeNote: () =>
    "This deletes the rating from the contractor's record for every agency.",
};

// ── SEMG mentions ────────────────────────────────────────────────────────────

const SEMGMentionFields = ({ form, readOnly }: FieldsProps) => (
  <Row>
    <Col md={6}>
      <TextField
        form={form}
        name="reportNumber"
        label="Report number"
        required
        readOnly={readOnly}
        placeholder="For example S/2019/858"
      />
    </Col>
    <Col md={6}>
      <TextField form={form} name="SEMGRegion" label="Location or region" readOnly={readOnly} />
    </Col>
    <Col md={4}>
      <TextField form={form} name="SEMGSection" label="Section" readOnly={readOnly} />
    </Col>
    <Col md={4}>
      <TextField form={form} name="SEMGPage" label="Page" readOnly={readOnly} />
    </Col>
    <Col md={4}>
      <TextField form={form} name="SEMGParagraph" label="Paragraph" readOnly={readOnly} />
    </Col>
    <Col md={12}>
      <TextField
        form={form}
        name="SEMGCommentary"
        label="Commentary"
        multiline
        readOnly={readOnly}
        placeholder="What the report says about this contractor"
      />
    </Col>
  </Row>
);

export const SEMG_MENTIONS: RecordKindConfig = {
  kind: "semg-mentions",
  title: "SEMG mentions",
  noun: "mention",
  permissions: relatedRecordPermissions("addSEMGMention", "editSEMGMention", "removeSEMGMention"),
  store: relatedRecordStore("semg-mentions"),
  records: (contractor) =>
    [...(contractor?.semgMentions || [])].sort((a, b) =>
      (a?.reportNumber || "").localeCompare(b?.reportNumber || "")
    ),
  summary: (record) => ({
    title: record?.reportNumber ? `Report ${record.reportNumber}` : "No report number",
    meta: joinMeta(
      record?.SEMGSection && `Section ${record.SEMGSection}`,
      record?.SEMGPage && `Page ${record.SEMGPage}`,
      record?.SEMGParagraph && `Para. ${record.SEMGParagraph}`
    ),
    avatar: { icon: "ri-file-text-line", tone: "danger" },
  }),
  emptyValues: {
    reportNumber: "",
    SEMGRegion: "",
    SEMGSection: "",
    SEMGPage: "",
    SEMGParagraph: "",
    SEMGCommentary: "",
  },
  toValues: (record) => ({
    reportNumber: record?.reportNumber || "",
    SEMGRegion: record?.SEMGRegion || "",
    SEMGSection: record?.SEMGSection || "",
    SEMGPage: record?.SEMGPage || "",
    SEMGParagraph: record?.SEMGParagraph || "",
    SEMGCommentary: record?.SEMGCommentary || "",
  }),
  toPayload: (values) => ({ ...values }),
  schema: Yup.object({
    reportNumber: Yup.string().trim().required("Enter the report number"),
  }),
  Fields: SEMGMentionFields,
  removeNote: () =>
    "This deletes the mention from the contractor. The SEMG report itself is not affected.",
};

// ── registrations ────────────────────────────────────────────────────────────
//
// Registrations are a repeatable component on the contractor rather than
// records of their own. The server changes one entry at a time under its own
// permissions (controllers/profile-details.ts), so other entries are kept as
// they are even when someone else saved in the meantime.

type Period = { count: number; unit: "years" | "months" };

/** Reads "5 years", "1 Years", "12 Months", "3 mo"; anything else is null. */
export const parsePeriod = (text?: string | null): Period | null => {
  const match = (text || "").trim().match(/^(\d+)\s*(years?|yrs?|months?|mos?)\.?$/i);
  if (!match) return null;
  const count = Number(match[1]);
  if (!count) return null;
  return { count, unit: /^y/i.test(match[2]) ? "years" : "months" };
};

const formatPeriod = ({ count, unit }: Period) =>
  `${count} ${count === 1 ? unit.slice(0, -1) : unit}`;

/** When a registration lapses: its date plus its length of validity. */
export const registrationExpiry = (date: any, validity?: string | null) => {
  const period = parsePeriod(validity);
  if (!date || !period) return null;
  return moment(date).add(period.count, period.unit).toDate();
};

const SOON_DAYS = 90;

export const registrationStatus = (expiry: Date | null): RecordStatus | null => {
  if (!expiry) return null;
  const days = moment(expiry).startOf("day").diff(moment().startOf("day"), "days");
  if (days < 0) return { label: "Expired", tone: "danger" };
  if (days <= SOON_DAYS) return { label: "Expires soon", tone: "warning" };
  return null;
};

const ValidUntil = ({ form }: { form: any }) => {
  const { registrationDate, validityCount, validityUnit } = form.values;
  const count = Number(validityCount);
  const expiry =
    registrationDate && count > 0
      ? registrationExpiry(registrationDate, formatPeriod({ count, unit: validityUnit }))
      : null;
  const status = registrationStatus(expiry);
  return (
    <div className="mb-3">
      <div className="form-label">Valid until</div>
      <div className="record-manager__derived" aria-live="polite">
        {expiry ? (
          <>
            <span className="fw-semibold">{moment(expiry).format("D MMM YYYY")}</span>
            {status ? (
              <span className={`badge bg-${status.tone}-subtle text-ink-${status.tone}`}>
                {status.label}
              </span>
            ) : null}
          </>
        ) : (
          <span className="text-ink-muted">
            Worked out from the date registered and the length of validity.
          </span>
        )}
      </div>
    </div>
  );
};

const RegistrationFields = ({ form, readOnly }: FieldsProps) => (
  <Row>
    <Col md={12}>
      <TextField
        form={form}
        name="registrationName"
        label="Registered name"
        required
        readOnly={readOnly}
        placeholder="As it appears on the certificate"
      />
    </Col>
    <Col md={6}>
      <TextField
        form={form}
        name="registrationNumber"
        label="Registration number"
        required
        readOnly={readOnly}
      />
    </Col>
    <Col md={6}>
      <TextField
        form={form}
        name="registrationType"
        label="Registration type"
        readOnly={readOnly}
        placeholder="For example Company, NGO, Business licence"
      />
    </Col>
    <Col md={6}>
      <CountryField
        form={form}
        name="countryOfRegistration"
        label="Country of registration"
        required
        readOnly={readOnly}
      />
    </Col>
    <Col md={6}>
      <DateField form={form} name="registrationDate" label="Date registered" readOnly={readOnly} />
    </Col>
    <Col md={6}>
      <PeriodField
        form={form}
        countName="validityCount"
        unitName="validityUnit"
        rawName="validityRaw"
        label="Length of validity"
        readOnly={readOnly}
      />
    </Col>
    <Col md={6}>
      <ValidUntil form={form} />
    </Col>
  </Row>
);

export const REGISTRATIONS: RecordKindConfig = {
  kind: "registrations",
  title: "Contractor registration",
  noun: "registration",
  permissions: {
    resource: "contractor",
    controller: "profile-details",
    add: "addRegistration",
    edit: "editRegistration",
    remove: "removeRegistration",
  },
  store: {
    add: async (contractor, payload) => {
      const response: any = await addContractorRegistration(contractor.id, payload);
      return response?.data?.id ?? null;
    },
    edit: async (contractor, record, payload) => {
      await editContractorRegistration(contractor.id, record.id, payload);
    },
    remove: async (contractor, record) => {
      await removeContractorRegistration(contractor.id, record.id);
    },
  },
  records: (contractor) =>
    [...(contractor?.contractorRegistration || [])].sort(
      (a, b) =>
        new Date(b?.registrationDate || 0).getTime() -
        new Date(a?.registrationDate || 0).getTime()
    ),
  summary: (record) => ({
    title: record?.registrationName || "Unnamed registration",
    meta: joinMeta(
      record?.registrationNumber && `No. ${record.registrationNumber}`,
      tidyCountry(record?.countryOfRegistration)
    ),
    avatar: { icon: "ri-government-line", tone: "primary" },
  }),
  dateLine: (record) => {
    const expiry = registrationExpiry(record?.registrationDate, record?.lengthOfValidity);
    if (expiry) {
      const day = moment(expiry).format("D MMM YYYY");
      return expiry < new Date() ? `Lapsed ${day}` : `Valid until ${day}`;
    }
    return record?.registrationDate
      ? `Registered ${moment(record.registrationDate).format("D MMM YYYY")}`
      : null;
  },
  status: (record) =>
    registrationStatus(registrationExpiry(record?.registrationDate, record?.lengthOfValidity)),
  emptyValues: {
    registrationName: "",
    registrationNumber: "",
    registrationType: "",
    countryOfRegistration: null,
    registrationDate: null,
    validityCount: "",
    validityUnit: "years",
    validityRaw: "",
  },
  toValues: (record) => {
    const period = parsePeriod(record?.lengthOfValidity);
    return {
      registrationName: record?.registrationName || "",
      registrationNumber: record?.registrationNumber || "",
      registrationType: record?.registrationType || "",
      countryOfRegistration: record?.countryOfRegistration || null,
      registrationDate: toDate(record?.registrationDate),
      validityCount: period ? String(period.count) : "",
      validityUnit: period?.unit || "years",
      validityRaw: period ? "" : (record?.lengthOfValidity || "").trim(),
    };
  },
  toPayload: (values) => {
    const count = Number(values.validityCount);
    return {
      registrationName: values.registrationName.trim(),
      registrationNumber: values.registrationNumber.trim(),
      registrationType: values.registrationType.trim() || null,
      countryOfRegistration: values.countryOfRegistration || null,
      registrationDate: toApiDate(values.registrationDate),
      lengthOfValidity:
        count > 0
          ? formatPeriod({ count, unit: values.validityUnit })
          : values.validityRaw || null,
    };
  },
  schema: Yup.object({
    registrationName: Yup.string().trim().required("Enter the registered name"),
    registrationNumber: Yup.string().trim().required("Enter the registration number"),
    countryOfRegistration: Yup.string().nullable().required("Choose the country of registration"),
    validityCount: Yup.number()
      .transform((value, original) => (original === "" ? undefined : value))
      .integer("Use a whole number")
      .positive("Use a number above zero"),
  }),
  Fields: RegistrationFields,
  removeNote: () => "This deletes the registration from the contractor's profile.",
};
